import { endpoints, types } from "magnetar-common";
import { useLocalStorage } from "@vueuse/core";
import { defineStore } from "pinia";
import { useAccountStore } from "@/model/account";
import { magApi } from "@/api-client";

type ColumnWidget = {
    name: string;
    id: string;
    data: Record<string, any>;
};

export const deckColumns = [
    "main",
    "widgets",
    "notifications",
    "tl",
    "antenna",
    "list",
    "mentions",
    "direct",
    "bookmarks",
] as const;

export type Column = {
    id: string;
    type: (typeof deckColumns)[number];
    name: string | null;
    width: number;
    widgets?: ColumnWidget[];
    active?: boolean;
    flexible?: boolean;
    antennaId?: string;
    listId?: string;
    includingTypes?: types.NotificationType[];
    tl?: "home" | "local" | "social" | "global";
};

export const useDeckStore = defineStore("deck", {
    state: () =>
        useLocalStorage(
            "mag/deck",
            {
                profile: "default",
                profiles: {
                    default: {
                        id: "default",
                        columns: [] as Column[],
                        layout: [] as Column["id"][][],
                        columnAlign: "left" as "left" | "right" | "center",
                        alwaysShowMainColumn: true,
                        navWindow: true,
                    },
                } as Record<
                    string,
                    {
                        id: string;
                        columns: Column[];
                        layout: Column["id"][][];
                        columnAlign: "left" | "right" | "center";
                        alwaysShowMainColumn: boolean;
                        navWindow: boolean;
                    }
                >,
            },
            {
                listenToStorageChanges: true,
                deep: true,
            },
        ),
    actions: {
        async fetchConfig() {
            const { current } = useAccountStore();
            if (!current) {
                return;
            }

            const deck = await magApi(
                endpoints.GetApplicationPreferences,
                {},
                { application: "@magnetar/client/deck/profiles" },
            ).catch((e) => {
                if (e?.["code"] === "mag::not_found") {
                    return {};
                }

                throw e;
            });

            // Migration pathway from Misskey
            if (deck && !("profile" in deck && "profiles" in deck)) {
                this.$patch({
                    profiles: deck,
                });
                return;
            }

            // Modern pathway
            this.$patch(deck ?? {});
        },
        async deleteProfile(key: string) {
            if (key === "default") {
                return;
            }

            this.$patch((state) => {
                delete state.profiles[key];

                if (key === state.profile) {
                    state.profile = "default";
                }
            });
        },
        async renameProfile(oldKey: string, newKey: string) {
            if (oldKey === newKey || newKey in this.profiles) {
                return;
            }

            this.$patch((state) => {
                state.profiles = {
                    ...Object.fromEntries(
                        Object.entries(state.profiles).map(([k, v]) => [
                            k === oldKey ? newKey : k,
                            v,
                        ]),
                    ),
                    default: state.profiles.default,
                };
            });
        },
        async addColumn(column: Column) {
            if (column.name === undefined) {
                column.name = null;
            }

            this.$patch((state) => {
                const profile = state.profiles[state.profile];
                profile.columns.push(column);
                profile.layout.push([column.id]);
            });
        },
        async removeColumn(id: Column["id"]) {
            this.$patch((state) => {
                const profile = state.profiles[state.profile];
                profile.columns = profile.columns.filter((c) => c.id !== id);
                profile.layout = profile.layout
                    .map((col) => col.filter((cId) => cId !== id))
                    .filter((col) => col.length);
            });
        },
        async swapColumns(a: Column["id"], b: Column["id"]) {
            this.$patch((state) => {
                const profile = state.profiles[state.profile];

                const aX = profile.layout.findIndex((ids) => ids.includes(a));
                const aY = profile.layout[aX].findIndex((id) => id === a);
                const bX = profile.layout.findIndex((ids) => ids.includes(b));
                const bY = profile.layout[bX].findIndex((id) => id === b);
                profile.layout[aX][aY] = b;
                profile.layout[bX][bY] = a;
            });
        },
        async swapLeftColumn(id: Column["id"]) {
            this.$patch((state) => {
                const profile = state.profiles[state.profile];

                const idx = profile.layout.findIndex((ids) => ids.includes(id));

                if (idx !== -1 && idx > 0) {
                    const left = profile.layout[idx - 1];
                    profile.layout[idx - 1] = profile.layout[idx];
                    profile.layout[idx] = left;
                }
            });
        },
        async swapRightColumn(id: Column["id"]) {
            this.$patch((state) => {
                const profile = state.profiles[state.profile];

                const idx = profile.layout.findIndex((ids) => ids.includes(id));

                if (idx !== -1 && idx < profile.layout.length - 1) {
                    const right = profile.layout[idx + 1];
                    profile.layout[idx + 1] = profile.layout[idx];
                    profile.layout[idx] = right;
                }
            });
        },
        async swapUpColumn(id: Column["id"]) {
            this.$patch((state) => {
                const profile = state.profiles[state.profile];

                const col = profile.layout.find((ids) => ids.includes(id));

                if (!col) {
                    return;
                }

                const rowIdx = col.findIndex((rId) => rId === id);

                if (rowIdx > 0) {
                    const up = col[rowIdx - 1];
                    col[rowIdx - 1] = col[rowIdx];
                    col[rowIdx] = up;
                }
            });
        },
        async swapDownColumn(id: Column["id"]) {
            this.$patch((state) => {
                const profile = state.profiles[state.profile];

                const col = profile.layout.find((ids) => ids.includes(id));

                if (!col) {
                    return;
                }

                const rowIdx = col.findIndex((rId) => rId === id);

                if (rowIdx < col.length - 1) {
                    const down = col[rowIdx + 1];
                    col[rowIdx + 1] = col[rowIdx];
                    col[rowIdx] = down;
                }
            });
        },
        async stackLeftColumn(id: Column["id"]) {
            this.$patch((state) => {
                const profile = state.profiles[state.profile];

                const colIdx = profile.layout.findIndex((ids) =>
                    ids.includes(id),
                );

                if (colIdx <= 0) {
                    return;
                }

                profile.layout[colIdx] = profile.layout[colIdx].filter(
                    (rId) => rId !== id,
                );
                profile.layout[colIdx - 1].push(id);
                profile.layout = profile.layout.filter((col) => col.length);
            });
        },
        async popRightColumn(id: Column["id"]) {
            this.$patch((state) => {
                const profile = state.profiles[state.profile];

                const colIdx = profile.layout.findIndex((ids) =>
                    ids.includes(id),
                );

                if (colIdx === -1) {
                    return;
                }

                profile.layout[colIdx] = profile.layout[colIdx].filter(
                    (rId) => rId !== id,
                );
                profile.layout.splice(colIdx + 1, 0, [id]);
                profile.columns = profile.columns.map((col) => {
                    if (
                        profile.layout[colIdx].includes(col.id) ||
                        col.id === id
                    ) {
                        col.active = true;
                    }

                    return col;
                });
            });
        },
        async updateColumn(id: Column["id"], column: Partial<Column>) {
            this.$patch((state) => {
                const profile = state.profiles[state.profile];

                const colIdx = profile.columns.findIndex(
                    (cId) => cId.id === id,
                );

                if (colIdx === -1) {
                    return;
                }

                profile.columns[colIdx] = {
                    ...profile.columns[colIdx],
                    ...column,
                };
            });
        },
    },
    getters: {
        currentProfile: (state) => state.profiles[state.profile],
        listProfiles: (state) => Object.keys(state.profiles),
    },
});
